import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import { SquarePenIcon } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Row, RowData, RowSelectionState } from '@tanstack/react-table';
import {
  createDefaultDevice,
  DeviceSchema,
  DEVICE_TYPES_NAMES_ARRAY,
  TDevice,
  TTreeDevice,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { useTranslation } from 'react-i18next';

export const DeviceTableToolbarEditButton = ({
  data,
  deviceInfo,
  hasSelectedRow,
  getSelectedTableRow,
  getSelectedDevice,
  setData,
  setSelectedRows,
}: {
  data: TTreeDevice[];
  deviceInfo: {
    companyId: string;
    plantId: string;
    timezone: string;
  };
  hasSelectedRow: boolean;
  getSelectedTableRow: () => Row<RowData> | undefined;
  getSelectedDevice: () => TTreeDevice | null;
  setData: (devices: TTreeDevice[]) => void;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(DeviceSchema),
    defaultValues: createDefaultDevice(deviceInfo),
  });

  const handleDeviceUpdate = (formData: TDevice) => {
    const selectedRow = getSelectedTableRow();
    if (!selectedRow) return;

    const updateDeviceNode = (
      deviceList: TTreeDevice[],
      targetPath: number[]
    ): TTreeDevice[] => {
      if (targetPath.length === 0) return deviceList;

      return deviceList.map((device, index) => {
        if (index === targetPath[0]) {
          if (targetPath.length === 1) {
            return {
              ...formData,
              subDevices: device.subDevices,
            };
          }
          return {
            ...device,
            subDevices: device.subDevices
              ? updateDeviceNode(device.subDevices, targetPath.slice(1))
              : device.subDevices,
          };
        }
        return device;
      });
    };

    const pathIndices = selectedRow.id.split('.').map(Number);
    const newData = updateDeviceNode(data, pathIndices);
    setData(newData);
    setSelectedRows({});
    setIsDialogOpen(false);
  };

  const selectedDevice = getSelectedDevice();
  useEffect(() => {
    if (selectedDevice) {
      form.reset(selectedDevice);
    }
  }, [selectedDevice, form]);

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex items-center gap-2 rounded-none py-5 font-normal hover:bg-gray-200"
          disabled={!hasSelectedRow}
        >
          <SquarePenIcon className="text-muted-foreground size-4" />
          <span>{t('common.buttons.editButton')}</span>
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        <Form {...form}>
          <CredenzaHeader>
            <CredenzaTitle>{t('devices.actions.editDevice')}</CredenzaTitle>
          </CredenzaHeader>
          <CredenzaBody>
            <CredenzaDescription className="hidden">
              {t('common.buttons.editAll')}
            </CredenzaDescription>
            <div className="flex flex-col gap-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('devices.deviceName')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="SN"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('devices.details.serialNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('plants.forms.basicDetails.plantType')}</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(value: 'Inverter' | 'Dongle') => {
                          field.onChange(value);
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue
                              placeholder={t('plants.forms.basicDetails.plantTypePlaceholder')}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DEVICE_TYPES_NAMES_ARRAY.map((deviceType) => (
                            <SelectItem key={deviceType} value={deviceType}>
                              {deviceType}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('devices.details.model')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('devices.details.supplier')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="showInDashboard"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0 pt-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel>
                      {t('devices.errors.deviceTableToolbar.showInDashboard')}
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-1">
                {(['companyId', 'plantId', 'deviceId', 'deviceName'] as (keyof TDevice)[]).map(
                  (fieldName) => (
                    <FormField
                      key={fieldName}
                      control={form.control}
                      name={fieldName}
                      render={() => (
                        <FormItem>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )
                )}
              </div>
            </div>
          </CredenzaBody>
          <CredenzaFooter>
            <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
              {t('common.buttons.cancelButton')}
            </Button>
            <Button type="button" onClick={form.handleSubmit(handleDeviceUpdate)}>
              {t('common.buttons.saveChangesButton')}
            </Button>
          </CredenzaFooter>
        </Form>
      </CredenzaContent>
    </Credenza>
  );
};
